import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, Mail, ShieldCheck, Truck, RefreshCcw } from "lucide-react";
import { NAV, CONTACT, IMG } from "@/data/site";
import PaymentIcons from "@/components/PaymentIcons";

const LEGAL = [
  { label: "Impressum", to: "/legal/impressum" },
  { label: "Datenschutz", to: "/legal/datenschutz" },
  { label: "AGB", to: "/legal/agb" },
  { label: "Versand", to: "/legal/versand" },
  { label: "Rückgabe", to: "/legal/rueckgabe" },
  { label: "Widerruf", to: "/legal/widerruf" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="relative bg-ink border-t border-white/10 overflow-hidden">
      <div className="container-x pt-20 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center gap-3">
              <img src={IMG.logo} alt="STARSOX" className="h-12 w-12 object-contain" />
              <span className="font-heading text-2xl">STARSOX</span>
            </Link>
            <p className="mt-5 text-white/50 max-w-sm leading-relaxed text-sm">
              Premium Grip-Socken – entwickelt für maximale Traktion, Komfort und Selbstvertrauen.
              Designed for Performance. Built for Champions.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <a href={CONTACT.instagramUrl} target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={`tel:${CONTACT.phone}`} data-testid="footer-phone" aria-label="Telefon" className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                <Phone className="w-5 h-5" />
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" aria-label="E-Mail" className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold mb-5">Shop</h4>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="text-white/60 hover:text-white transition-colors text-sm">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold mb-5">Rechtliches</h4>
            <ul className="space-y-3">
              {LEGAL.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} data-testid={`footer-${n.to.split("/").pop()}`} className="text-white/60 hover:text-white transition-colors text-sm">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold mb-5">Kontakt</h4>
            <ul className="space-y-3 text-sm text-white/60">
              <li><a href={CONTACT.instagramUrl} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">{CONTACT.instagram}</a></li>
              <li><a href={`tel:${CONTACT.phone}`} className="hover:text-white transition-colors">{CONTACT.phoneDisplay}</a></li>
              <li><a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors">{CONTACT.email}</a></li>
              <li>Versand: nur Deutschland</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Trust + payment strip */}
      <div className="container-x pb-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, t: "Sichere Zahlung", s: "SSL-verschlüsselt via Stripe" },
            { icon: Truck, t: "Schneller Versand", s: "2–3 Werktage · ab 50 € gratis" },
            { icon: RefreshCcw, t: "14 Tage Rückgabe", s: "Einkauf ohne Risiko" },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.t} className="flex items-center gap-4 border border-white/10 rounded-2xl bg-carbon px-6 py-5">
                <span className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-gold" /></span>
                <div>
                  <p className="font-heading text-base uppercase tracking-wide leading-none">{b.t}</p>
                  <p className="text-white/45 text-xs mt-1.5">{b.s}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Massive display type */}
      <div className="select-none pointer-events-none px-2">
        <div className="font-heading text-[19vw] leading-[0.8] text-white/[0.04] text-center tracking-tight">STARSOX</div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-5">
          <p className="text-white/40 text-xs order-2 md:order-1">© {new Date().getFullYear()} STARSOX · starsox.de · Alle Rechte vorbehalten.</p>
          <div className="flex items-center gap-4 order-1 md:order-2">
            <span className="text-white/40 text-[11px] font-mono uppercase tracking-widest hidden sm:inline">Bezahlen mit</span>
            <PaymentIcons testId="payment-icons-footer" />
          </div>
        </div>
      </div>
    </footer>
  );
}
